import React from 'react';
import { Loader2, CheckCircle } from 'lucide-react';

import { UI_TEXT } from '../../config/ui-text';
import { isImageFinal, ServerImageLike } from '../../features/humanSurvey/photoUploadSessionSync';

const P = UI_TEXT.HUMAN_SURVEY.PHOTO;

interface PhotoExtractionStatusProps {
  image: ServerImageLike;
  /** Heads the rejection reason. Respondents never see "AI note" (ui-text.ts); only the researcher page passes it. */
  noteLabel?: string;
}

/**
 * The one extraction-status badge for a survey photo, shared by the desktop QR panel, the phone
 * upload page, the in-browser camera upload and the researcher results page.
 *
 * An `uploading` placeholder renders nothing: the file has not reached the server, so "Analysing…"
 * would be premature, and the surfaces that can be in that state show their own upload overlay.
 */
export const PhotoExtractionStatus: React.FC<PhotoExtractionStatusProps> = ({
  image,
  noteLabel = UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES.PHOTO_REVIEW_NOTE,
}) => {
  const productCount = image.extracted_products?.length ?? 0;

  if (productCount > 0) {
    return (
      <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
        <CheckCircle className="w-3 h-3" />
        {productCount} product{productCount === 1 ? '' : 's'}
      </span>
    );
  }

  if (image.extraction_status === 'failed') {
    // `rejection_reason` is nullable: a reason-less failure is still final, never "Analysing…".
    return (
      <div className="text-xs text-amber-800">
        <p className="font-medium">{noteLabel}</p>
        <p>{image.rejection_reason || P.REJECTED_NO_REASON}</p>
      </div>
    );
  }

  if (image.extraction_status !== 'uploading' && !isImageFinal(image)) {
    return (
      <span role="status" className="text-xs text-gray-600 inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />
        {P.ANALYSING}
      </span>
    );
  }

  return null;
};
